import { useState } from "react";
import {
    AlertCircle,
    ChevronRight,
    DollarSign,
    Download,
    ExternalLink,
    Eye,
    Layers,
    MousePointerClick,
    Play,
    Send,
    TrendingUp,
    Users,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    useBroadcastReport,
    useGroupedBroadcastReport,
} from "../api/queries";
import { useDashboardStore } from "../store/dashboardStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { Segmented } from "../components/Segmented";
import { BadgeHi } from "../components/BadgeHi";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import { cur, deltaDir, fmt, fmtDelta, fmtPct } from "../lib/format";
import { correctFailureRate } from "../lib/numbers";
import { cn } from "@/lib/utils";
import type {
    BroadcastReportResponse,
    GroupedBroadcastReportResponse,
} from "../types/reports";

interface BroadcastReportViewProps {
    bcId?: string;
    contentHash?: string;
}

function is404(err: unknown): boolean {
    if (!err || typeof err !== "object") return false;
    const e = err as { response?: { status?: number }; status?: number };
    return e.response?.status === 404 || e.status === 404;
}

export function BroadcastReportView({
    bcId,
    contentHash,
}: BroadcastReportViewProps) {
    const setView = useDashboardStore((s) => s.setView);
    const breadcrumbs = useDashboardStore((s) => s.breadcrumbs);

    // Grouped report — only fires when contentHash present
    const groupedQuery = useGroupedBroadcastReport(contentHash);

    // Fallback to individual: enabled when either no contentHash, OR grouped 404'd
    const groupedFailed404 = is404(groupedQuery.error);
    const useIndividual = !contentHash || groupedFailed404;
    const individualQuery = useBroadcastReport(useIndividual ? bcId : undefined);

    const isGroupedView = !!contentHash && !groupedFailed404;

    // Pick the active query result
    const data: BroadcastReportResponse | GroupedBroadcastReportResponse | undefined =
        isGroupedView ? groupedQuery.data : individualQuery.data;
    const error = isGroupedView
        ? groupedQuery.error && !groupedFailed404
            ? groupedQuery.error
            : null
        : individualQuery.error;
    const isLoading = isGroupedView
        ? groupedQuery.isLoading && !groupedQuery.data
        : individualQuery.isLoading && !individualQuery.data;
    const refetch = () => {
        if (isGroupedView) groupedQuery.refetch();
        else individualQuery.refetch();
    };
    const loading = isLoading && !data;

    const b = data?.broadcast;
    const kpis = data?.kpis;
    const clicksByHour = data?.clicksByHourSinceLaunch ?? [];
    const clicksByHourOfDay = data?.clicksByHourOfDay ?? [];
    // "ofday" = por hora do dia (00–23) · "decay" = horas desde o disparo
    const [hourMode, setHourMode] = useState<"ofday" | "decay">("ofday");
    const perPage = data?.perPage ?? [];
    const perButton = data?.perButton ?? [];
    const preview = data?.preview;
    const pagesCount =
        (data as GroupedBroadcastReportResponse | undefined)?.broadcast
            ?.pagesCount ??
        b?.pagesCount ??
        perPage.length ??
        1;

    // Failure rate fallback
    const sendsN = Number(kpis?.sends ?? 0);
    const failuresN = Number(kpis?.failures ?? 0);
    const totalAttempts = sendsN + failuresN;
    const failureRate = correctFailureRate(failuresN, sendsN);

    const headerTitle = loading
        ? "Carregando…"
        : b?.name || breadcrumbs.at(-1)?.label || "Broadcast";
    const headerSubtitle = isGroupedView
        ? `Conteúdo idêntico disparado em ${pagesCount} páginas · métricas somadas`
        : b
          ? `${b.pageName || "—"} · ${
                b.sentAt ? b.sentAt.slice(0, 10) : "—"
            } · ${b.pagesCount || 1} ${
                (b.pagesCount ?? 1) > 1 ? "páginas" : "página"
            }`
          : "";

    const hero = (
        <PageHeader
            icon={Send}
            title={headerTitle}
            subtitle={headerSubtitle}
            right={
                <>
                    {isGroupedView && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500/90 text-white px-3 py-1.5 rounded-lg">
                            <Layers className="w-3.5 h-3.5" />
                            {pagesCount} páginas agregadas
                        </span>
                    )}
                    <button className="inline-flex items-center gap-1.5 text-xs font-medium bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg text-white">
                        <Download className="w-3.5 h-3.5" />
                        CSV
                    </button>
                </>
            }
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={refetch} />
            </PageShell>
        );
    }

    return (
        <PageShell hero={hero}>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KpiCard
                    icon={DollarSign}
                    iconColor="text-emerald-500"
                    label="Receita"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" +
                            Number(cur(kpis?.revenue)).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })
                        )
                    }
                    delta={fmtDelta(kpis?.revenue)}
                    deltaDir={deltaDir(kpis?.revenue)}
                />
                <KpiCard
                    icon={Send}
                    iconColor="text-blue-500"
                    label="Enviadas"
                    value={loading ? <SkelText /> : fmt(sendsN)}
                />
                <KpiCard
                    icon={MousePointerClick}
                    iconColor="text-violet-500"
                    label="Cliques"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            fmt(Number(kpis?.clicks ?? 0))
                        )
                    }
                    sub={kpis?.ctr != null ? `CTR ${fmtPct(kpis.ctr)}` : ""}
                />
                <KpiCard
                    icon={AlertCircle}
                    iconColor="text-rose-500"
                    label="Falhas"
                    value={loading ? <SkelText /> : fmtPct(failureRate)}
                    sub={`${fmt(failuresN)} de ${fmt(totalAttempts)}`}
                />
                <KpiCard
                    icon={Layers}
                    iconColor="text-amber-500"
                    label="Páginas"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            fmt(pagesCount)
                        )
                    }
                    sub={
                        kpis?.rpm != null
                            ? `RPM $${Number(kpis.rpm).toFixed(2)}`
                            : ""
                    }
                />
            </div>

            {/* Chart + Preview */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
                <SectionCard
                    icon={TrendingUp}
                    title="Cliques por hora"
                    subtitle={
                        hourMode === "ofday"
                            ? "por hora do dia · 0h–23h"
                            : "horas desde o disparo · 0h–23h+"
                    }
                    right={
                        <Segmented<"ofday" | "decay">
                            value={hourMode}
                            onChange={setHourMode}
                            options={[
                                { v: "ofday", l: "Hora do dia" },
                                { v: "decay", l: "Decay" },
                            ]}
                        />
                    }
                >
                    {loading ? (
                        <DashSkeleton h={256} />
                    ) : hourMode === "ofday" ? (
                        <BroadcastClicksByHourOfDay
                            data={clicksByHourOfDay}
                            totalClicks={Number(kpis?.clicks ?? 0)}
                            byDateHour={data?.clicksByDateHour}
                        />
                    ) : (
                        <BroadcastHourlyClicks
                            data={clicksByHour}
                            totalClicks={Number(kpis?.clicks ?? 0)}
                        />
                    )}
                </SectionCard>

                <SectionCard icon={Eye} title="Preview do template">
                    {loading ? (
                        <DashSkeleton h={240} />
                    ) : !preview ? (
                        <EmptyMsg />
                    ) : (
                        <BroadcastTemplatePreview preview={preview} />
                    )}
                </SectionCard>
            </div>

            {/* Per-page breakdown */}
            {perPage.length > 0 ? (
                <SectionCard
                    icon={Layers}
                    title="Detalhamento por página"
                    subtitle={
                        isGroupedView
                            ? `${perPage.length} páginas · ordenado por receita`
                            : "quando o broadcast disparou em várias"
                    }
                    right={
                        isGroupedView ? (
                            <BadgeHi color="amber">{pagesCount} páginas</BadgeHi>
                        ) : null
                    }
                >
                    <div className="space-y-2">
                        {[...perPage]
                            .sort((a, p) => (p.revenue || 0) - (a.revenue || 0))
                            .map((p, i) => {
                                const clickable = !!p.broadcastId;
                                const Tag = clickable ? "button" : "div";
                                return (
                                    <Tag
                                        key={p.pageId || p.broadcastId || i}
                                        onClick={
                                            clickable
                                                ? () =>
                                                      setView(
                                                          {
                                                              name: "broadcast",
                                                              id: p.broadcastId,
                                                          },
                                                          p.pageName ||
                                                              "BC #" +
                                                                  p.broadcastId,
                                                          { keepParent: true }
                                                      )
                                                : undefined
                                        }
                                        className={cn(
                                            "w-full text-left flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-lg border border-gray-100 transition-colors",
                                            clickable &&
                                                "hover:border-blue-200 hover:bg-blue-50/30 cursor-pointer group"
                                        )}
                                    >
                                        <span className="text-[10px] font-mono text-gray-400 w-6 shrink-0">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p
                                                className={cn(
                                                    "text-sm font-semibold text-gray-800 truncate",
                                                    clickable &&
                                                        "group-hover:text-blue-600"
                                                )}
                                            >
                                                {p.pageName || "—"}
                                            </p>
                                            <p className="text-[11px] text-gray-400 font-mono">
                                                #{p.pageId}
                                                {p.broadcastId
                                                    ? ` · bc #${p.broadcastId}`
                                                    : ""}
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-4 gap-4 shrink-0">
                                            <div className="text-right">
                                                <p className="text-[10px] text-gray-400 uppercase">
                                                    Enviadas
                                                </p>
                                                <p className="text-sm font-semibold text-gray-800 font-mono">
                                                    {fmt(p.sends)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] text-gray-400 uppercase">
                                                    Cliques
                                                </p>
                                                <p className="text-sm font-semibold text-blue-600 font-mono">
                                                    {fmt(p.clicks)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] text-gray-400 uppercase">
                                                    CTR
                                                </p>
                                                <span
                                                    className={cn(
                                                        "inline-flex text-xs font-semibold px-2 py-0.5 rounded-full",
                                                        p.ctr >= 8
                                                            ? "bg-emerald-50 text-emerald-700"
                                                            : p.ctr >= 3
                                                              ? "bg-blue-50 text-blue-700"
                                                              : "bg-gray-100 text-gray-600"
                                                    )}
                                                >
                                                    {p.ctr != null
                                                        ? p.ctr.toFixed(1) + "%"
                                                        : "—"}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] text-gray-400 uppercase">
                                                    Receita
                                                </p>
                                                <p className="text-sm font-semibold text-gray-900 font-mono">
                                                    $
                                                    {Number(
                                                        p.revenue
                                                    ).toLocaleString("pt-BR", {
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                        {clickable && (
                                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 shrink-0" />
                                        )}
                                    </Tag>
                                );
                            })}
                    </div>
                </SectionCard>
            ) : isGroupedView && !loading ? (
                <SectionCard
                    icon={Layers}
                    title="Detalhamento por página"
                    right={<BadgeHi color="amber">{pagesCount} páginas</BadgeHi>}
                >
                    <EmptyMsg />
                </SectionCard>
            ) : null}

            {/* Per-button breakdown */}
            {perButton.length > 0 && (
                <SectionCard icon={MousePointerClick} title="Cliques por botão">
                    <div className="space-y-2">
                        {perButton.map((btn, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50"
                            >
                                <div className="min-w-0 flex items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-800 truncate">
                                        {btn.text}
                                    </span>
                                    {btn.type && (
                                        <span className="text-[10px] text-gray-400 uppercase font-medium">
                                            {btn.type}
                                        </span>
                                    )}
                                </div>
                                <div className="text-right shrink-0 flex items-center gap-3">
                                    <span className="text-sm font-bold text-blue-600 font-mono">
                                        {fmt(btn.clicks)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </SectionCard>
            )}

            {/* Optional unique-leads chip */}
            {!loading && kpis?.uniqueLeads != null && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Users className="w-3.5 h-3.5 text-gray-400" />
                    <span>
                        Leads únicos:{" "}
                        <span className="font-mono font-semibold text-gray-900">
                            {fmt(kpis.uniqueLeads)}
                        </span>
                    </span>
                </div>
            )}
        </PageShell>
    );
}

function EmptyMsg() {
    return (
        <div className="text-center py-8">
            <AlertCircle className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem dados</p>
        </div>
    );
}

// ── Hourly clicks chart (0h–23h) ─────────────────────────
interface BroadcastHourlyClicksProps {
    data: Array<{ hourOffset: number; clicks: number }>;
    totalClicks: number;
}

function BroadcastHourlyClicks({
    data,
    totalClicks,
}: BroadcastHourlyClicksProps) {
    // O dado é DECAIMENTO desde o disparo (`hourOffset` = horas após launch),
    // NÃO hora do dia. Backend manda `hourOffset`; legacy `hour` é ignorado
    // pra evitar interpretação errada (pico em "00h" era confundido com
    // meia-noite — na verdade é a 1ª hora após o disparo).
    const byHour = new Map<number, number>(
        (data || []).map((p) => {
            const raw = p as { hourOffset?: number; clicks: number };
            const h = Number(raw.hourOffset ?? 0);
            return [h, Number(p.clicks) || 0];
        })
    );
    const padded: Array<{ h: string; hRaw: number; v: number }> = [];
    for (let i = 0; i < 24; i++) {
        // Label: "0h" = primeira hora após o disparo, "+1h", "+2h"...
        // O último bucket (i=23) acumula 23h+ no decaimento (vide backend).
        const label =
            i === 0 ? "0h" : i === 23 ? "23h+" : `+${i}h`;
        padded.push({
            h: label,
            hRaw: i,
            v: byHour.get(i) || 0,
        });
    }
    const sum = padded.reduce((s, p) => s + p.v, 0);

    if (totalClicks === 0 && sum === 0) {
        return (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                <MousePointerClick className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm font-medium text-gray-500">
                    Nenhum clique registrado
                </p>
                <p className="text-[11px] mt-1">
                    esse broadcast teve{" "}
                    <span className="font-mono">0 cliques</span> no período
                </p>
            </div>
        );
    }

    const peak = [...padded].sort((a, b) => b.v - a.v)[0];

    return (
        <>
            <div className="h-56">
                <ResponsiveContainer>
                    <BarChart
                        data={padded}
                        margin={{ top: 5, right: 5, bottom: 0, left: -10 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f1f5f9"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="h"
                            tick={{ fontSize: 10, fill: "#64748b" }}
                            tickLine={false}
                            axisLine={false}
                            interval={2}
                        />
                        <YAxis
                            tick={{ fontSize: 10, fill: "#64748b" }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v: number) =>
                                v >= 1000 ? (v / 1000).toFixed(0) + "k" : String(v)
                            }
                        />
                        <Tooltip
                            contentStyle={{
                                borderRadius: 8,
                                border: "1px solid #e5e7eb",
                                fontSize: 12,
                            }}
                            formatter={(v) => [
                                fmt(Number(v ?? 0)),
                                "cliques",
                            ]}
                            labelFormatter={(l) =>
                                l === "0h"
                                    ? "1ª hora pós-disparo"
                                    : l === "23h+"
                                      ? "23h+ pós-disparo"
                                      : `${l} pós-disparo`
                            }
                        />
                        <Bar dataKey="v" radius={[4, 4, 0, 0]}>
                            {padded.map((p) => (
                                <Cell
                                    key={p.h}
                                    fill={
                                        peak &&
                                        p.hRaw === peak.hRaw &&
                                        peak.v > 0
                                            ? "#f59e0b"
                                            : "#3b82f6"
                                    }
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            {peak && peak.v > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        <span className="text-gray-500">Pico:</span>
                        <span className="font-mono font-semibold text-gray-900">
                            {peak.hRaw === 0
                                ? "1ª hora"
                                : peak.hRaw === 23
                                  ? "23h+"
                                  : `+${peak.hRaw}h`}{" "}
                            · {fmt(peak.v)} cliques
                        </span>
                    </div>
                    <span className="text-gray-500">
                        Total{" "}
                        <span className="font-mono font-semibold text-gray-900">
                            {fmt(sum)}
                        </span>
                    </span>
                </div>
            )}
        </>
    );
}

// ── Template preview ─────────────────────────────────────
interface PreviewShape {
    text?: string | null;
    title?: string | null;
    subtitle?: string | null;
    mediaUrl?: string | null;
    mediaType?: string | null;
    buttons?: Array<{ text: string; type?: string; url?: string }>;
}

function BroadcastTemplatePreview({ preview }: { preview: PreviewShape }) {
    const hasMedia = !!preview.mediaUrl;
    const hasTitle = !!preview.title;
    const hasSubtitle = !!preview.subtitle;
    const hasText =
        !!preview.text &&
        preview.text.trim() !== "" &&
        !preview.text.startsWith("http");
    const buttons = preview.buttons || [];
    const isVideo = preview.mediaType === "video";

    const isEmpty =
        !hasMedia && !hasTitle && !hasSubtitle && !hasText && buttons.length === 0;
    if (isEmpty) {
        return (
            <div className="flex flex-col items-center justify-center text-gray-400 py-8">
                <AlertCircle className="w-6 h-6 mb-2 opacity-50" />
                <p className="text-sm text-gray-500">Preview indisponível</p>
                <p className="text-[11px] mt-0.5">
                    o template não retornou conteúdo
                </p>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-3">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                {hasMedia && (
                    <div
                        className="bg-slate-200 overflow-hidden"
                        style={{ aspectRatio: "16/10" }}
                    >
                        {isVideo ? (
                            <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                                <Play className="w-10 h-10 text-white" />
                            </div>
                        ) : (
                            <img
                                src={preview.mediaUrl!}
                                alt=""
                                className="object-cover w-full h-full"
                                onError={(e) => {
                                    const img = e.currentTarget;
                                    const parent = img.parentElement;
                                    if (parent)
                                        parent.innerHTML =
                                            '<div class="w-full h-full flex items-center justify-center text-slate-400 text-xs">imagem indisponível</div>';
                                }}
                            />
                        )}
                    </div>
                )}
                {(hasTitle || hasSubtitle || hasText) && (
                    <div className="px-3 py-2.5 space-y-1">
                        {hasTitle && (
                            <p className="text-sm font-bold text-gray-900 leading-snug">
                                {preview.title}
                            </p>
                        )}
                        {hasSubtitle && (
                            <p className="text-xs text-gray-600 leading-snug">
                                {preview.subtitle}
                            </p>
                        )}
                        {hasText && (
                            <p className="text-xs text-gray-700 whitespace-pre-wrap leading-snug">
                                {preview.text}
                            </p>
                        )}
                    </div>
                )}
                {buttons.length > 0 && (
                    <div className="border-t border-gray-100">
                        {buttons.map((btn, i) => (
                            <div
                                key={i}
                                className="px-3 py-2 text-center text-blue-600 text-xs font-semibold border-b last:border-0 border-gray-100 truncate flex items-center justify-center gap-1.5"
                            >
                                {btn.type === "url" && (
                                    <ExternalLink className="w-3 h-3 opacity-60" />
                                )}
                                {btn.text}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {/* Meta footer */}
            <div className="mt-2.5 flex items-center justify-between text-[10px] text-gray-400 px-1">
                <span className="font-mono uppercase">
                    {preview.mediaType || "template"}
                </span>
                {hasMedia && (
                    <span
                        className="font-mono truncate max-w-[180px]"
                        title={preview.mediaUrl ?? undefined}
                    >
                        {(preview.mediaUrl || "").split("/").pop()}
                    </span>
                )}
            </div>
        </div>
    );
}

// ── Cliques por HORA DO DIA (00..23) ──────────────────────
// Default da UI — mostra qual horário do dia os leads mais clicam
// (vs `BroadcastHourlyClicks` que é decay desde o disparo).
interface BroadcastClicksByHourOfDayProps {
    data: Array<{ hour: number; clicks: number }>;
    totalClicks: number;
    byDateHour?: Array<{ date: string; hour: number; clicks: number }>;
}

function BroadcastClicksByHourOfDay({
    data,
    totalClicks,
    byDateHour,
}: BroadcastClicksByHourOfDayProps) {
    const byHour = new Map<number, number>(
        (data || []).map((p) => [Number(p.hour) || 0, Number(p.clicks) || 0])
    );
    const padded: Array<{ h: string; hRaw: number; v: number }> = [];
    for (let i = 0; i < 24; i++) {
        padded.push({
            h: String(i).padStart(2, "0") + "h",
            hRaw: i,
            v: byHour.get(i) || 0,
        });
    }
    const sum = padded.reduce((s, p) => s + p.v, 0);

    // Tooltip mostra breakdown por data só quando filtro cobre múltiplos dias.
    // 1 dia → tooltip atual (sem data). N dias → lista "DD/MM: K cliques".
    const uniqueDates = new Set((byDateHour || []).map((r) => r.date));
    const isMultiDay = uniqueDates.size > 1;
    const dateHourMap = new Map<number, Array<{ date: string; clicks: number }>>();
    if (isMultiDay) {
        for (const r of byDateHour || []) {
            const h = Number(r.hour) || 0;
            const arr = dateHourMap.get(h) || [];
            arr.push({ date: r.date, clicks: Number(r.clicks) || 0 });
            dateHourMap.set(h, arr);
        }
    }
    const fmtDate = (iso: string) => {
        // "2026-06-16" → "16/06"
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
        return m ? `${m[3]}/${m[2]}` : iso;
    };

    if (totalClicks === 0 && sum === 0) {
        return (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                <MousePointerClick className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm font-medium text-gray-500">
                    Nenhum clique registrado
                </p>
            </div>
        );
    }

    const peak = [...padded].sort((a, b) => b.v - a.v)[0];

    return (
        <>
            <div className="h-56">
                <ResponsiveContainer>
                    <BarChart
                        data={padded}
                        margin={{ top: 5, right: 5, bottom: 0, left: -10 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f1f5f9"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="h"
                            tick={{ fontSize: 10, fill: "#64748b" }}
                            tickLine={false}
                            axisLine={false}
                            interval={2}
                        />
                        <YAxis
                            tick={{ fontSize: 10, fill: "#64748b" }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v: number) =>
                                v >= 1000 ? (v / 1000).toFixed(0) + "k" : String(v)
                            }
                        />
                        <Tooltip
                            cursor={{ fill: "rgba(0,0,0,0.04)" }}
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;
                                const item = payload[0]?.payload as { hRaw: number; v: number };
                                const total = item?.v ?? 0;
                                const byDate = isMultiDay
                                    ? (dateHourMap.get(item?.hRaw) || []).sort((a, b) => a.date.localeCompare(b.date))
                                    : [];
                                return (
                                    <div className="rounded-lg border border-gray-200 bg-white shadow-sm px-3 py-2 text-xs">
                                        <div className="font-semibold text-gray-900 mb-1">{label} do dia</div>
                                        {isMultiDay && byDate.length > 0 ? (
                                            <>
                                                <div className="space-y-0.5">
                                                    {byDate.map((b) => (
                                                        <div key={b.date} className="flex items-center justify-between gap-3">
                                                            <span className="text-gray-500">{fmtDate(b.date)}</span>
                                                            <span className="font-mono font-medium text-gray-900">{fmt(b.clicks)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-1 pt-1 border-t border-gray-100 flex items-center justify-between gap-3">
                                                    <span className="text-gray-500">Total</span>
                                                    <span className="font-mono font-semibold text-gray-900">{fmt(total)}</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-gray-600">
                                                cliques: <span className="font-mono font-semibold text-gray-900">{fmt(total)}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                        <Bar dataKey="v" radius={[4, 4, 0, 0]}>
                            {padded.map((p) => (
                                <Cell
                                    key={p.h}
                                    fill={
                                        peak && p.hRaw === peak.hRaw && peak.v > 0
                                            ? "#f59e0b"
                                            : "#3b82f6"
                                    }
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            {peak && peak.v > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        <span className="text-gray-500">Pico:</span>
                        <span className="font-mono font-semibold text-gray-900">
                            {String(peak.hRaw).padStart(2, "0")}h · {fmt(peak.v)}{" "}
                            cliques
                        </span>
                    </div>
                    <span className="text-gray-500">
                        Total{" "}
                        <span className="font-mono font-semibold text-gray-900">
                            {fmt(sum)}
                        </span>
                    </span>
                </div>
            )}
        </>
    );
}
